import AutoCompleteSelect from "component/Common/AutoCompleteSelect";
import { useFlightContext } from "hooks/flight/useFlightContext";
import useFlightSearchParams from "hooks/flight/useFlightSearchParams";
import useApiClient from "hooks/useApiClient";
import useAutoComplete from "hooks/useAutoComplete";
import { FaPlaneArrival, FaPlaneDeparture } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import Flatpickr from "react-flatpickr";
import { dateFormater, dayFromDate } from "helpers/common";
import { IoMdClose } from "react-icons/io";
import { useState } from "react";

const mapFlightResponseToOptions = (data) => {
    return data.map((airport) => ({
        id: airport.id,
        city_name: airport.city_name,
        iata: airport.iata,
        country_name: airport.country_name,
        airport_name: airport.name
            ? airport.name
            : airport.city_name + " Airport",
    }));
};

const mapTopWeightedAirportsToOptions = (data) => {
    return data.map((airport) => ({
        id: airport?.id ?? airport?.iata,
        city_name: airport?.city_name ?? airport?.city ?? "",
        iata: airport?.iata ?? airport?.id ?? "",
        country_name: airport?.country_name ?? airport?.country ?? "",
        airport_name:
            airport?.name ??
            airport?.airport_name ??
            ((airport?.city_name ?? airport?.city ?? "") + " Airport"),
    }));
};

const MultiDestinationTab = () => {
    const { flightSearchParams } = useFlightContext();

    return (
        <div className="tab-pane fade show ">
            <div className="row">
                <div className="col-lg-12">
                    <div className="oneway_search_form">
                        <form action="#!" className="flex flex-col gap-3">
                            {[
                                ...Array(
                                    flightSearchParams?.numberOfBounds || 1
                                ).keys(),
                            ].map((i) => {
                                return <Segment key={`seg ${i}`} index={i} />;
                            })}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default MultiDestinationTab;

const Segment = ({ index }) => {
    const { urls, api } = useApiClient();
    const { t, i18n } = useTranslation();
    const [topAirportsCache, setTopAirportsCache] = useState(null);
    const {
        term: departureTerm,
        results: departureResults,
        loading: departureLoading,
        handleInputChange: handleDepartureInputChange,
        setResults: setDepartureResults,
        setTerm: setDepartureTerm,
    } = useAutoComplete({
        url: urls.flight.airportAutocomplete,
        mapResponseToOptions: mapFlightResponseToOptions,
    });

    const {
        term: arrivalTerm,
        results: arrivalResults,
        loading: arrivalLoading,
        handleInputChange: handleArrivalInputChange,
        setResults: setArrivalResults,
        setTerm: setArrivalTerm,
    } = useAutoComplete({
        url: urls.flight.airportAutocomplete,
        mapResponseToOptions: mapFlightResponseToOptions,
    });

    const { updateFlightSearchParams } = useFlightSearchParams();
    const { flightSearchParams, setFlightSearchParams } = useFlightContext();

    const fetchTopWeightedAirports = async (limit = 6) => {
        if (topAirportsCache && topAirportsCache.length > 0) {
            return topAirportsCache;
        }
        try {
            const res = await api.get(urls.flight.topWeightedAirports, {
                params: {
                    limit,
                    language: i18n.language,
                },
            });
            const raw = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data)
                ? res.data
                : [];
            const options = mapTopWeightedAirportsToOptions(raw);
            setTopAirportsCache(options);
            return options;
        } catch (error) {
            console.error("Error fetching top weighted airports:", error);
            return [];
        }
    };

    const preloadDepartureTopAirports = async () => {
        if (departureTerm.trim().length > 0) return;
        const options = await fetchTopWeightedAirports();
        setDepartureResults(options);
    };

    const preloadArrivalTopAirports = async () => {
        if (arrivalTerm.trim().length > 0) return;
        const options = await fetchTopWeightedAirports();
        setArrivalResults(options);
    };

    const deleteSegment = (index) => {
        if (flightSearchParams.numberOfBounds < 2) {
            return;
        }
        const newFlightSearchParams = { ...flightSearchParams };
        delete newFlightSearchParams[`departureOption${index + 1}`];
        delete newFlightSearchParams[`arrivalOption${index + 1}`];
        delete newFlightSearchParams[`departureDate${index + 1}`];
        newFlightSearchParams.numberOfBounds =
            flightSearchParams.numberOfBounds - 1;

        for (let i = index + 1; i <= flightSearchParams.numberOfBounds; i++) {
            newFlightSearchParams[`departureOption${i}`] =
                flightSearchParams[`departureOption${i + 1}`];
            newFlightSearchParams[`arrivalOption${i}`] =
                flightSearchParams[`arrivalOption${i + 1}`];
            newFlightSearchParams[`departureDate${i}`] =
                flightSearchParams[`departureDate${i + 1}`];
        }

        delete newFlightSearchParams[
            `departureOption${flightSearchParams.numberOfBounds}`
        ];
        delete newFlightSearchParams[
            `arrivalOption${flightSearchParams.numberOfBounds}`
        ];
        delete newFlightSearchParams[
            `departureDate${flightSearchParams.numberOfBounds}`
        ];

        setFlightSearchParams(newFlightSearchParams);
    };

    return (
        <div className="row">
            {flightSearchParams.numberOfBounds > 1 ? (
                <div className="col-lg-12 flex justify-between items-center md:hidden px-3 mb-2">
                    <div className="">vol {index + 1}</div>
                    <button type="button" className="btn btn-danger">
                        <IoMdClose
                            size={20}
                            onClick={() => deleteSegment(index)}
                        />
                    </button>
                </div>
            ) : null}
            <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                <div className="flight_Search_boxed border-none ">
                    <p>{t("whereFrom")}</p>
                    <AutoCompleteSelect
                        name="flight"
                        options={departureResults}
                        value={departureTerm}
                        onInputChange={handleDepartureInputChange}
                        isLoading={departureLoading}
                        onMenuOpen={preloadDepartureTopAirports}
                        selectedOption={
                            flightSearchParams[`departureOption${index + 1}`]
                        }
                        onChange={(selectedOption) => {
                            updateFlightSearchParams(
                                `departureOption${index + 1}`,
                                selectedOption
                            );
                            if (!selectedOption) {
                                setDepartureTerm("");
                                fetchTopWeightedAirports().then((options) => {
                                    setDepartureResults(options);
                                });
                            }
                        }}
                    />
                    {flightSearchParams[`departureOption${index + 1}`] ? (
                        <span className="truncate text-ellipsis">
                            {
                                flightSearchParams[
                                    `departureOption${index + 1}`
                                ]?.airport_name
                            }
                        </span>
                    ) : (
                        <span className="text-transparent">.</span>
                    )}
                    <div className="plan_icon_posation">
                        <FaPlaneDeparture size={22} />
                    </div>
                </div>
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12 col-12">
                <div className="flight_Search_boxed  border-none ">
                    <p>{t("whereTo")}</p>
                    <AutoCompleteSelect
                        name={"flight"}
                        options={arrivalResults}
                        value={arrivalTerm}
                        onInputChange={handleArrivalInputChange}
                        isLoading={arrivalLoading}
                        onMenuOpen={preloadArrivalTopAirports}
                        selectedOption={
                            flightSearchParams[`arrivalOption${index + 1}`]
                        }
                        onChange={(selectedOption) => {
                            updateFlightSearchParams(
                                `arrivalOption${index + 1}`,
                                selectedOption
                            );
                            if (!selectedOption) {
                                setArrivalTerm("");
                                fetchTopWeightedAirports().then((options) => {
                                    setArrivalResults(options);
                                });
                            }
                        }}
                    />
                    {flightSearchParams[`arrivalOption${index + 1}`] ? (
                        <span className="truncate text-ellipsis">
                            {
                                flightSearchParams[`arrivalOption${index + 1}`]
                                    ?.airport_name
                            }
                        </span>
                    ) : (
                        <span className="text-transparent">.</span>
                    )}
                    <div className="plan_icon_posation">
                        <FaPlaneArrival size={22} />
                    </div>
                </div>
            </div>
            <div
                className={`${
                    flightSearchParams.numberOfBounds === 1
                        ? "col-lg-4"
                        : "col-lg-3"
                } col-md-6 col-sm-12 col-12`}
            >
                <div className="form_search_date h-full">
                    <div className="flight_Search_boxed date_flex_area">
                        <div className="Journey_date w-full">
                            <p>{t("journeyDate")}</p>
                            <div className="">
                                <Flatpickr
                                    key={"ow"}
                                    value={
                                        flightSearchParams[
                                            `departureDate${index + 1}`
                                        ] || null
                                    }
                                    onChange={(dates) => {
                                        updateFlightSearchParams(
                                            `departureDate${index + 1}`,
                                            dateFormater(dates[0])
                                        );
                                    }}
                                    options={{
                                        mode: "single",
                                        altInput: true,
                                        altFormat: "F j Y",
                                        dateFormat: "Y-m-d",
                                        minDate: "today",
                                        showMonths: 2,
                                        animate: true,
                                        onOpen: function(selectedDates, dateStr, instance) {
                                            instance.nextMonthNav.addEventListener('click', function(e) {
                                            e.preventDefault();
                                            instance.changeMonth(1);
                                            });
                                            instance.prevMonthNav.addEventListener('click', function(e) {
                                            e.preventDefault();
                                            instance.changeMonth(-1);
                                            });
                                        }
                                    }}
                                    
                                    placeholder={t("selectDate")}
                                    className="w-full bg-transparent"
                                />
                            </div>
                            {flightSearchParams[`departureDate${index + 1}`] ? (
                                <span className="truncate text-ellipsis">
                                    {dayFromDate(
                                        flightSearchParams[
                                            `departureDate${index + 1}`
                                        ]
                                    )}
                                </span>
                            ) : (
                                <span className="text-transparent">.</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {flightSearchParams.numberOfBounds > 1 ? (
                <div className="col-lg-1 col-md-6 col-sm-12 col-12  items-center hidden md:flex">
                    <button
                        type="button"
                        className="flex items-center justify-center w-full h-full text-red-500"
                    >
                        <IoMdClose
                            size={22}
                            onClick={() => deleteSegment(index)}
                        />
                    </button>
                </div>
            ) : null}
        </div>
    );
};
